import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import useStoreSubdomain from '@/hooks/useStoreSubdomain';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, ShoppingCart, Package, Minus, Plus, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const fmt = (n) => new Intl.NumberFormat('id-ID').format(n || 0);

export default function BundleDetail() {
  const { bundleId } = useParams();
  const subdomain = useStoreSubdomain();
  const navigate = useNavigate();
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [cart, setCart] = useState([]);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    const fetchBundle = async () => {
      try {
        const res = await fetch(`${BACKEND}/api/store/${subdomain}/bundles/${bundleId}`);
        if (!res.ok) throw new Error();
        const data = await res.json();
        setBundle(data);
        document.title = `${data.name} — Bundle`;
      } catch { setBundle(null); }
      finally { setLoading(false); }
    };
    fetchBundle();
    const saved = localStorage.getItem(`cart_${subdomain}`);
    if (saved) setCart(JSON.parse(saved));
  }, [subdomain, bundleId]);

  const savingsPercent = bundle && bundle.original_total > bundle.bundle_price
    ? Math.round((1 - bundle.bundle_price / bundle.original_total) * 100) : 0;

  const addToCart = () => {
    const existing = cart.find(c => c.bundle_id === bundle.id);
    let newCart;
    if (existing) {
      newCart = cart.map(c => c.bundle_id === bundle.id ? { ...c, quantity: c.quantity + quantity } : c);
    } else {
      const firstImage = bundle.images?.[0]
        || bundle.items_populated?.[0]?.product?.images?.[0] || '';
      newCart = [...cart, {
        bundle_id: bundle.id,
        name: bundle.name,
        price: bundle.bundle_price,
        quantity,
        image: firstImage,
        is_bundle: true,
        items: (bundle.items || []).map(i => ({ product_id: i.product_id, quantity: i.quantity })),
      }];
    }
    setCart(newCart);
    localStorage.setItem(`cart_${subdomain}`, JSON.stringify(newCart));
    setAdded(true);
    toast.success('Bundle ditambahkan ke keranjang');
    setTimeout(() => setAdded(false), 2000);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  if (!bundle) return (
    <div className="max-w-lg mx-auto p-8 text-center">
      <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
      <h2 className="text-xl font-bold mb-2">Bundle tidak ditemukan</h2>
      <Link to={`/store/${subdomain}`}><Button variant="outline"><ArrowLeft className="w-4 h-4 mr-2" /> Kembali ke Toko</Button></Link>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-8 space-y-6">
      <Link to={`/store/${subdomain}`} className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="w-4 h-4 mr-1" /> Kembali ke Toko
      </Link>

      {/* Bundle Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Badge className="bg-purple-100 text-purple-700"><Package className="w-3 h-3 mr-1" /> Bundle</Badge>
          {savingsPercent > 0 && <Badge className="bg-red-100 text-red-700">Hemat {savingsPercent}%</Badge>}
        </div>
        <h1 className="text-2xl md:text-3xl font-bold">{bundle.name}</h1>
        {bundle.description && <p className="text-muted-foreground">{bundle.description}</p>}
      </div>

      {/* Price */}
      <div className="flex items-baseline gap-3">
        {bundle.original_total > bundle.bundle_price && (
          <span className="text-lg line-through text-muted-foreground">Rp {fmt(bundle.original_total)}</span>
        )}
        <span className="text-3xl font-bold text-green-600">Rp {fmt(bundle.bundle_price)}</span>
      </div>

      {/* Products in Bundle */}
      <Card>
        <CardContent className="p-4 space-y-1">
          <h3 className="font-semibold mb-3">Produk dalam bundle ({(bundle.items_populated || []).length} item)</h3>
          <div className="divide-y">
            {(bundle.items_populated || []).map((it, i) => {
              const p = it.product;
              if (!p) return null;
              const unitPrice = p.sale_price || p.price;
              return (
                <div key={i} className="flex items-center gap-3 py-3">
                  {p.images?.[0] ? (
                    <img src={p.images[0]} alt={p.name} className="w-14 h-14 rounded-md object-cover" />
                  ) : (
                    <div className="w-14 h-14 rounded-md bg-gray-100 flex items-center justify-center"><Package className="w-6 h-6 text-gray-400" /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{p.name}</div>
                    <div className="text-xs text-muted-foreground">{it.quantity > 1 ? `${it.quantity}× ` : ''}Rp {fmt(unitPrice)}</div>
                  </div>
                  <div className="text-sm font-medium">Rp {fmt(unitPrice * (it.quantity || 1))}</div>
                </div>
              );
            })}
          </div>
          <div className="pt-3 border-t flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Total harga satuan</span>
            <span className="font-medium">Rp {fmt(bundle.original_total)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm font-semibold text-green-700">Harga bundle</span>
            <span className="font-bold text-green-700">Rp {fmt(bundle.bundle_price)}</span>
          </div>
          {bundle.original_total > bundle.bundle_price && (
            <div className="flex justify-between items-center text-red-600">
              <span className="text-sm font-semibold">Anda hemat</span>
              <span className="font-bold">Rp {fmt(bundle.original_total - bundle.bundle_price)}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quantity & Add to Cart */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        <div className="flex items-center gap-2 border rounded-md">
          <Button type="button" variant="ghost" size="sm" onClick={() => setQuantity(q => Math.max(1, q - 1))} disabled={quantity <= 1}>
            <Minus className="w-4 h-4" />
          </Button>
          <span className="w-10 text-center font-medium">{quantity}</span>
          <Button type="button" variant="ghost" size="sm" onClick={() => setQuantity(q => q + 1)}>
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        <Button size="lg" className="flex-1 sm:flex-none" onClick={addToCart}>
          {added ? <Check className="w-5 h-5 mr-2" /> : <ShoppingCart className="w-5 h-5 mr-2" />}
          {added ? 'Ditambahkan!' : 'Tambah ke Keranjang'}
        </Button>
        <Button size="lg" variant="outline" onClick={() => { addToCart(); navigate(`/store/${subdomain}/checkout`); }}>
          Beli Sekarang
        </Button>
      </div>

      {/* Stock info */}
      {bundle.available_stock !== undefined && bundle.available_stock < 10 && (
        <p className="text-sm text-orange-600">Stok terbatas: tersisa {bundle.available_stock}</p>
      )}
    </div>
  );
}
